import type { AnnotationQuote, AnnotationResolution } from '../annotationTypes';

interface MatchCandidate {
  endOffset: number;
  startOffset: number;
}

function normalizeOffsets(
  quote: Pick<AnnotationQuote, 'endOffset' | 'startOffset'>,
  textLength: number,
) {
  const startOffset =
    typeof quote.startOffset === 'number' && Number.isFinite(quote.startOffset)
      ? Math.max(0, Math.trunc(quote.startOffset))
      : null;
  const endOffset =
    typeof quote.endOffset === 'number' && Number.isFinite(quote.endOffset)
      ? Math.min(textLength, Math.trunc(quote.endOffset))
      : null;

  if (startOffset === null || endOffset === null || endOffset <= startOffset) {
    return null;
  }

  return {
    endOffset,
    startOffset,
  };
}

function findExactMatches(text: string, exact: string): MatchCandidate[] {
  if (exact.length === 0) {
    return [];
  }

  const matches: MatchCandidate[] = [];
  let searchIndex = 0;

  while (searchIndex <= text.length) {
    const matchIndex = text.indexOf(exact, searchIndex);

    if (matchIndex === -1) {
      break;
    }

    matches.push({
      endOffset: matchIndex + exact.length,
      startOffset: matchIndex,
    });
    searchIndex = matchIndex + 1;
  }

  return matches;
}

function scoreContextMatch(
  text: string,
  candidate: MatchCandidate,
  quote: AnnotationQuote,
) {
  const prefixMatches =
    quote.prefix.length > 0
      ? text.slice(
          Math.max(0, candidate.startOffset - quote.prefix.length),
          candidate.startOffset,
        ) === quote.prefix
      : false;
  const suffixMatches =
    quote.suffix.length > 0
      ? text.slice(candidate.endOffset, candidate.endOffset + quote.suffix.length) ===
        quote.suffix
      : false;

  if (prefixMatches && suffixMatches) {
    return 2;
  }

  if (prefixMatches || suffixMatches) {
    return 1;
  }

  return 0;
}

export function resolveTextQuote(
  text: string,
  quote: AnnotationQuote,
): AnnotationResolution {
  const exact = quote.exact;

  if (exact.length > 0) {
    const exactMatches = findExactMatches(text, exact);
    const hasContext = quote.prefix.length > 0 || quote.suffix.length > 0;

    if (hasContext) {
      const scoredMatches = exactMatches
        .map((candidate) => ({
          candidate,
          score: scoreContextMatch(text, candidate, quote),
        }))
        .sort((left, right) => right.score - left.score);

      const bestMatch = scoredMatches.find((entry) => entry.score === 2)?.candidate;

      if (bestMatch) {
        return {
          confidence: 'high',
          endOffset: bestMatch.endOffset,
          startOffset: bestMatch.startOffset,
          status: 'resolved',
          strategy: 'context',
        };
      }
    }

    if (exactMatches.length === 1) {
      return {
        confidence: 'medium',
        endOffset: exactMatches[0].endOffset,
        startOffset: exactMatches[0].startOffset,
        status: 'resolved',
        strategy: 'exact',
      };
    }

    if (exactMatches.length > 1) {
      const normalizedLegacyOffsets = normalizeOffsets(quote, text.length);

      if (normalizedLegacyOffsets) {
        const nearestMatch = [...exactMatches].sort((left, right) => {
          const leftDistance = Math.abs(
            left.startOffset - normalizedLegacyOffsets.startOffset,
          );
          const rightDistance = Math.abs(
            right.startOffset - normalizedLegacyOffsets.startOffset,
          );

          return leftDistance - rightDistance;
        })[0];

        return {
          confidence: 'medium',
          endOffset: nearestMatch.endOffset,
          startOffset: nearestMatch.startOffset,
          status: 'resolved',
          strategy: 'exact',
        };
      }

      return {
        confidence: 'medium',
        endOffset: exactMatches[0].endOffset,
        startOffset: exactMatches[0].startOffset,
        status: 'resolved',
        strategy: 'exact',
      };
    }
  }

  const normalizedOffsets = normalizeOffsets(quote, text.length);

  if (normalizedOffsets) {
    return {
      confidence: 'low',
      endOffset: normalizedOffsets.endOffset,
      startOffset: normalizedOffsets.startOffset,
      status: 'resolved',
      strategy: 'offset',
    };
  }

  return {
    confidence: 'none',
    status: 'orphaned',
    strategy: 'orphaned',
  };
}
